import { EventEmitter, Injectable } from "@angular/core";
import { CookieKeys } from "src/domain/constants";
import { CookieService } from "./cookie.service";
import { EventTagUnion } from "src/interfaces/gtm-interfaces";
import { IConsentFormResult } from "src/interfaces/interfaces";
import { ConsentFormCategory } from "src/enums/ConsentFormCategory";
import { BehaviorSubject } from "rxjs";
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  map,
} from "rxjs/operators";

type CamelCase<T extends string> = T extends `${infer Prefix}_${infer Suffix}`
  ? `${Capitalize<Prefix>}${CamelCase<Suffix>}`
  : Capitalize<T>;

type CamelCaseNoFirst<T extends string> =
  T extends `${infer Prefix}_${infer Suffix}`
    ? `${Lowercase<Prefix>}${CamelCase<Suffix>}`
    : Lowercase<T>;

export type ConsentState = {
  [key in ConsentFormCategory]: boolean | null;
};

export const MINIMAL_CONSENT: IConsentFormResult = {
  required: true,
  functional: false,
  performance: false,
  marketing: false,
};

export const FULL_CONSENT: IConsentFormResult = {
  required: true,
  functional: true,
  performance: true,
  marketing: true,
};

export enum ConsentServiceStatus {
  LOADING = "loading",
  READY = "ready",
}

function canUseGtm(state: ConsentState): boolean {
  return Object.entries(state).some(([k, v]) => k !== "required" && v === true);
}

function canUseHotjar(state: ConsentState): boolean {
  return state.performance === true;
}

function makeChildSubject<T, K extends keyof T>(
  root: BehaviorSubject<T>,
  key: K,
): BehaviorSubject<T[K]> {
  const sub = new BehaviorSubject<T[K]>(root.value[key]);
  root
    .pipe(
      distinctUntilKeyChanged(key),
      map(({ [key]: val }) => val),
    )
    .subscribe(sub);
  return sub;
}

export const DEFAULT_CONSENT: ConsentState = {
  required: null,
  functional: null,
  marketing: null,
  performance: null,
};

@Injectable({
  providedIn: "root",
})
export class ConsentService {
  public readonly dialog = new EventEmitter<"open" | "close">();

  public consentState = new BehaviorSubject(DEFAULT_CONSENT);

  public readonly requiredConsent: BehaviorSubject<boolean | null>;
  public readonly functionalConsent: BehaviorSubject<boolean | null>;
  public readonly marketingConsent: BehaviorSubject<boolean | null>;
  public readonly performanceConsent: BehaviorSubject<boolean | null>;
  public readonly gtmEnabled: BehaviorSubject<boolean>;
  public readonly hotjarEnabled: BehaviorSubject<boolean>;

  private getConsentFromCookie(
    consentType: ConsentFormCategory,
  ): boolean | null {
    const cookieValue = this.cookieService.getCookie(
      `${CookieKeys.consentPrefix}${consentType}`,
    );
    if (cookieValue === "0") return false;
    if (cookieValue === "1") return true;
    return null;
  }

  public readonly status = new BehaviorSubject<ConsentServiceStatus>(
    ConsentServiceStatus.LOADING,
  );

  constructor(private cookieService: CookieService) {
    this.requiredConsent = makeChildSubject(
      this.consentState,
      ConsentFormCategory.REQUIRED,
    );
    this.functionalConsent = makeChildSubject(
      this.consentState,
      ConsentFormCategory.FUNCTIONAL,
    );
    this.marketingConsent = makeChildSubject(
      this.consentState,
      ConsentFormCategory.MARKETING,
    );
    this.performanceConsent = makeChildSubject(
      this.consentState,
      ConsentFormCategory.PERFORMANCE,
    );

    this.gtmEnabled = new BehaviorSubject(canUseGtm(this.consentState.value));
    this.consentState
      .pipe(map(canUseGtm), distinctUntilChanged())
      .subscribe(this.gtmEnabled);
    this.hotjarEnabled = new BehaviorSubject(
      canUseHotjar(this.consentState.value),
    );
    this.consentState
      .pipe(map(canUseHotjar), distinctUntilChanged())
      .subscribe(this.hotjarEnabled);
    const restoredState: ConsentState = {
      required: this.getConsentFromCookie(ConsentFormCategory.REQUIRED) ?? null,
      functional:
        this.getConsentFromCookie(ConsentFormCategory.FUNCTIONAL) ?? null,
      performance:
        this.getConsentFromCookie(ConsentFormCategory.PERFORMANCE) ?? null,
      marketing:
        this.getConsentFromCookie(ConsentFormCategory.MARKETING) ?? null,
    };
    this.consent(restoredState).finally(() => {
      this.status.next(ConsentServiceStatus.READY);
    });
  }

  private async consent(consent?: ConsentState): Promise<void> {
    if (consent) {
      this.consentState.next({
        ...this.consentState.value,
        ...consent,
      });
    }
  }

  private async updateCookies(consentForm: IConsentFormResult) {
    (
      Object.entries(consentForm) as (readonly [
        keyof typeof consentForm,
        (typeof consentForm)[keyof typeof consentForm],
      ])[]
    ).forEach(([type, value]) => {
      if (typeof value === "boolean") {
        this.cookieService.setCookie(
          `${CookieKeys.consentPrefix}${type}`,
          value ? "1" : "0",
          365,
        );
      }
    });
  }

  public consentFromForm(consentForm: IConsentFormResult): Promise<void> {
    this.updateCookies(consentForm);
    const { required, functional, marketing, performance } = consentForm;
    return this.consent({
      required,
      functional,
      marketing,
      performance,
    });
  }

  isTrackingEventAllowed(item: EventTagUnion): boolean;
  isTrackingEventAllowed() {
    if (!this.gtmEnabled.value) return false;
    return true;
  }

  public openConsentDialog(): void {
    this.dialog.emit("open");
  }
}

ConsentService.prototype satisfies {
  readonly [key in `${CamelCaseNoFirst<ConsentFormCategory>}Consent`]: BehaviorSubject<
    boolean | null
  >;
};
